// Betrieb › Radios (h-ui): alle Radios der Installation für den Betreiber.
//
// Liste mit Suche und Filter, ein neues Radio anlegen, und je Radio eine
// Detailseite: Tarif und Abweichungen, Nutzung, Kontakt/Notiz, Sperren,
// Löschen vormerken und Support-Zugriff. Nur die Rolle „Betreiber“
// (super_admin) kommt hierher – der Server prüft jede Anfrage selbst.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { betriebApi } from "../../api";
import { useAuth } from "../../auth/AuthProvider";
import { CopyValue, errorText } from "../../components/streaming-ui";
import { Meldungen, Sym } from "../../components/social-ui";
import { Badge, Button, Callout, Card, EmptyState, Page, PageHeader, Skeleton, Tabs } from "../../components/ui";
import { StatusBadgeRadio, bytesText, datumText, zahlText } from "../../components/hosting-ui";
import type { BetriebRadio, RadioStatus, Tarif } from "../../types";
import { NurBetreiber } from "./NurBetreiber";

type Filter = "alle" | RadioStatus;

export function RadiosPage(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [radios, setRadios] = useState<BetriebRadio[] | null>(null);
  const [tarife, setTarife] = useState<Tarif[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [suche, setSuche] = useState("");
  const [filter, setFilter] = useState<Filter>("alle");
  const [tarifFilter, setTarifFilter] = useState("");
  const [neuOffen, setNeuOffen] = useState(false);

  const laden = (): void => {
    betriebApi.radios().then(setRadios).catch((err) => { setRadios([]); setFehler(errorText(err)); });
  };

  useEffect(() => {
    if (user?.role !== "super_admin") return;
    laden();
    betriebApi.tarife().then(setTarife).catch(() => undefined);
  }, [user?.role]);

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return (radios ?? []).filter((r) =>
      (filter === "alle" || r.status === filter) &&
      (!tarifFilter || r.tarif.key === tarifFilter) &&
      (!q || `${r.name} ${r.slug} ${r.kontakt_email ?? ""}`.toLowerCase().includes(q)));
  }, [radios, suche, filter, tarifFilter]);

  if (user?.role !== "super_admin") return <NurBetreiber titel="Radios" />;

  const anzahl = (s: RadioStatus): number => (radios ?? []).filter((r) => r.status === s).length;
  const tarifName = (key: string): string => tarife.find((t) => t.key === key)?.name ?? key;

  return (
    <Page>
      <PageHeader
        eyebrow="Betrieb"
        title="Radios"
        description="Alle Radios dieser Installation: Zustand, Tarif, Nutzung. Hier legst du neue Radios an und kümmerst dich um bestehende."
        actions={
          <>
            {/* h-umzug */}
            <Button variant="ghost" onClick={() => navigate("/betrieb/import")}>
              <Sym name="up" />Importieren
            </Button>
            <Button variant={neuOffen ? "secondary" : "primary"} onClick={() => setNeuOffen((o) => !o)}>
              <Sym name={neuOffen ? "x" : "plus"} />{neuOffen ? "Abbrechen" : "Neues Radio"}
            </Button>
          </>
        }
      />

      <Meldungen error={fehler} />

      {neuOffen && (
        <NeuesRadio
          tarife={tarife}
          onAbbrechen={() => setNeuOffen(false)}
          onAngelegt={() => laden()}
          onOeffnen={(id) => navigate(`/betrieb/radios/${id}`)}
        />
      )}

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <Tabs<Filter>
          value={filter}
          onChange={setFilter}
          items={[
            { value: "alle", label: "Alle" },
            { value: "aktiv", label: "Aktiv" },
            { value: "gesperrt", label: "Gesperrt", count: anzahl("gesperrt") },
            { value: "loeschen_vorgemerkt", label: "Löschung", count: anzahl("loeschen_vorgemerkt") },
          ]}
        />
        <div className="flex min-w-0 flex-1 gap-2">
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Name, Kürzel oder Kontakt suchen …"
            className="input min-w-0 flex-1"
            aria-label="Radios durchsuchen"
          />
          <select value={tarifFilter} onChange={(e) => setTarifFilter(e.target.value)} className="input w-auto max-w-[40%]" aria-label="Nach Tarif filtern">
            <option value="">Alle Tarife</option>
            {tarife.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
        </div>
      </div>

      <Card padded={false} title="Radios" actions={radios ? <Badge>{gefiltert.length} von {radios.length}</Badge> : undefined}>
        {radios === null && (
          <div className="space-y-2 p-5" aria-label="Wird geladen …">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        )}
        {radios !== null && gefiltert.length === 0 && (
          <div className="p-4">
            <EmptyState
              icon={<Sym name="radio" className="h-5 w-5" />}
              title={radios.length === 0 ? "Noch keine Radios" : "Nichts gefunden"}
              description={radios.length === 0 ? "Leg das erste Radio an – die Radioleitung bekommt eine Einladung per Mail." : "Suche oder Filter anpassen."}
            />
          </div>
        )}

        {/* Breit: Tabelle */}
        {gefiltert.length > 0 && (
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-default text-left text-xs text-muted">
                  <th className="px-5 py-2.5 font-medium">Radio</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 font-medium">Tarif</th>
                  <th className="px-3 py-2.5 text-right font-medium">Hörer jetzt</th>
                  <th className="px-3 py-2.5 text-right font-medium">Sendet</th>
                  <th className="px-3 py-2.5 text-right font-medium">Speicher</th>
                  <th className="px-5 py-2.5 text-right font-medium">Letzte Anmeldung</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-default">
                {gefiltert.map((r) => (
                  <tr key={r.id} className="cursor-pointer hover:bg-surface-hover" onClick={() => navigate(`/betrieb/radios/${r.id}`)}>
                    <td className="px-5 py-3">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Link to={`/betrieb/radios/${r.id}`} className="font-medium text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{r.name}</Link>
                        <Merkmale r={r} />
                      </span>
                      <div className="text-xs text-muted">{r.slug}{r.kontakt_email ? ` · ${r.kontakt_email}` : ""}</div>
                    </td>
                    <td className="px-3 py-3"><StatusBadgeRadio status={r.status} /></td>
                    <td className="px-3 py-3 text-secondary">{r.tarif.name ?? tarifName(r.tarif.key)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{zahlText(r.hoerer_jetzt)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{sendetText(r)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{bytesText(r.speicher.mediathek_bytes + r.speicher.bilder_bytes)}</td>
                    <td className="px-5 py-3 text-right text-secondary">{datumText(r.letzte_anmeldung, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Schmal: Karten */}
        {gefiltert.length > 0 && (
          <ul className="divide-y divide-default md:hidden">
            {gefiltert.map((r) => (
              <li key={r.id}>
                <Link to={`/betrieb/radios/${r.id}`} className="block px-4 py-3 hover:bg-surface-hover">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5"><span className="truncate font-medium text-primary">{r.name}</span><Merkmale r={r} /></div>
                      <div className="truncate text-xs text-muted">{r.slug} · {r.tarif.name ?? tarifName(r.tarif.key)}</div>
                    </div>
                    <StatusBadgeRadio status={r.status} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary">
                    <span><span className="text-muted">Hörer</span> <b className="tabular-nums">{zahlText(r.hoerer_jetzt)}</b></span>
                    <span><span className="text-muted">Sendet</span> <b className="tabular-nums">{sendetText(r)}</b></span>
                    <span><span className="text-muted">Speicher</span> <b className="tabular-nums">{bytesText(r.speicher.mediathek_bytes + r.speicher.bilder_bytes)}</b></span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}

/** Kleine Etiketten neben dem Namen: eigenes Radio, individuelle Grenzen. */
function Merkmale({ r }: { r: BetriebRadio }): JSX.Element {
  return (
    <>
      {r.hat_betreiber && <Badge tone="brand">dein Radio</Badge>}
      {r.hat_abweichungen && <Badge tone="info">eigene Grenzen</Badge>}
    </>
  );
}

/** „2/3“: laufende von aktiven Kanälen; ohne aktiven Kanal ein Strich. */
function sendetText(r: BetriebRadio): string {
  if (r.sendezustand.aktiv === 0) return "–";
  return `${r.sendezustand.laufend}/${r.sendezustand.aktiv}`;
}

/** Aus dem Namen ein Kürzel für die Adresse machen: „Radio Maar-Eifel“ → „radio-maar-eifel“. */
export function slugAus(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFKD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function NeuesRadio({ tarife, onAbbrechen, onAngelegt, onOeffnen }: {
  tarife: Tarif[];
  onAbbrechen: () => void;
  onAngelegt: () => void;
  onOeffnen: (id: string) => void;
}): JSX.Element {
  const vorgabe = tarife.find((t) => t.vorgabe)?.key ?? tarife[0]?.key ?? "";
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugHand, setSlugHand] = useState(false);
  const [tarif, setTarif] = useState(vorgabe);
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [kontakt, setKontakt] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<{ id: string; mail: boolean; link: string | null; fehler?: string; gueltigBis?: string } | null>(null);

  useEffect(() => { if (!tarif && vorgabe) setTarif(vorgabe); }, [vorgabe, tarif]);

  const slugOk = /^[a-z0-9][a-z0-9-]{1,39}$/.test(slug);
  const bereit = name.trim().length >= 2 && slugOk && !!tarif && adminName.trim().length > 0 && /.+@.+\..+/.test(adminEmail);

  const anlegen = async (): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await betriebApi.anlegen({
        name: name.trim(), slug, tarif, admin_email: adminEmail.trim(), admin_name: adminName.trim(),
        ...(kontakt.trim() ? { kontakt_email: kontakt.trim() } : {}),
      });
      setErgebnis({
        id: r.radio?.id ?? "",
        mail: r.einladung.verschickt,
        link: r.einladung.link ?? null,
        fehler: r.einladung.fehler,
        gueltigBis: r.einladung.gueltig_bis,
      });
      onAngelegt();
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setLaeuft(false);
    }
  };

  if (ergebnis) {
    return (
      <Card title={`„${name}“ ist angelegt`} className="mb-5 animate-fade-in">
        {ergebnis.mail ? (
          <Callout tone="success" title="Einladung verschickt">
            {adminName} bekommt eine Mail an <b>{adminEmail}</b> und legt darüber das eigene Passwort fest.
          </Callout>
        ) : (
          <Callout tone="warning" title="Keine Mail verschickt">
            {ergebnis.fehler ? `Die Mail ließ sich nicht verschicken (${ergebnis.fehler}).` : "Diese Installation hat keinen Mailversand eingerichtet."}{" "}
            Gib {adminName} diesen Link weiter – darüber wird das eigene Passwort festgelegt. Er gilt nur einmal
            {ergebnis.gueltigBis ? ` und bis ${datumText(ergebnis.gueltigBis, true)}` : ""}.
          </Callout>
        )}
        {ergebnis.link && <div className="mt-4"><CopyValue label="Einladung" value={ergebnis.link} /></div>}
        <div className="mt-4 flex flex-wrap gap-2">
          {ergebnis.id && <Button variant="primary" onClick={() => onOeffnen(ergebnis.id)}>Radio öffnen</Button>}
          <Button onClick={onAbbrechen}>Schließen</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Neues Radio" description="Legt das Radio und seinen ersten Zugang (Radioleitung) an." className="mb-5 animate-fade-in">
      {fehler && <Callout tone="danger" className="mb-4">{fehler}</Callout>}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="field-label">Name des Radios</span>
          <input className="input" value={name} placeholder="Radio Maar-Eifel"
            onChange={(e) => { setName(e.target.value); if (!slugHand) setSlug(slugAus(e.target.value)); }} />
        </label>
        <label className="block">
          <span className="field-label">Kürzel (Adresse)</span>
          <input className="input font-mono" value={slug} placeholder="radio-maar-eifel"
            onChange={(e) => { setSlugHand(true); setSlug(e.target.value.toLowerCase()); }} />
          <span className={`field-hint block ${slug && !slugOk ? "!text-red-600 dark:!text-red-400" : ""}`}>
            Kleinbuchstaben, Ziffern und Bindestriche, 2–40 Zeichen. Später nicht mehr änderbar.
          </span>
        </label>
        <label className="block">
          <span className="field-label">Tarif</span>
          <select className="input" value={tarif} onChange={(e) => setTarif(e.target.value)}>
            {tarife.filter((t) => t.aktiv || t.key === tarif).map((t) => (
              <option key={t.key} value={t.key}>{t.name}{t.vorgabe ? " (Vorgabe)" : ""}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="field-label">Kontakt für Rechnung & Betrieb <span className="font-normal text-muted">(optional)</span></span>
          <input className="input" type="email" value={kontakt} placeholder="verein@beispiel.de" onChange={(e) => setKontakt(e.target.value)} />
        </label>
        <label className="block">
          <span className="field-label">Name der Radioleitung</span>
          <input className="input" value={adminName} placeholder="Anna Beispiel" onChange={(e) => setAdminName(e.target.value)} />
        </label>
        <label className="block">
          <span className="field-label">E-Mail der Radioleitung</span>
          <input className="input" type="email" value={adminEmail} placeholder="anna@beispiel.de" onChange={(e) => setAdminEmail(e.target.value)} />
          <span className="field-hint block">Hierhin geht die Einladung zum Passwort-Setzen.</span>
        </label>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button variant="primary" disabled={!bereit || laeuft} onClick={() => void anlegen()}>
          {laeuft ? "Wird angelegt …" : "Radio anlegen"}
        </Button>
        <Button variant="ghost" onClick={onAbbrechen}>Abbrechen</Button>
      </div>
    </Card>
  );
}
